import { getAntiforgeryToken } from "../api/http";

// Cliente de Chat › Equipo (T33–T34). Los mismos datos que los componentes Blazor leían del servidor (CompanyOperatingClient,
// A5 de Messaging e Identity), ahora por /api/portal/team, que vuelve a decidir el acceso en cada pedido. Cada pedido lleva
// la empresa con la que se cargó la pantalla: si la sesión cambió de empresa, el servidor responde 409 y no se muestra nada
// de la otra. Los tipos son los registros C# del servidor (Services/CompanyOperatingClient.cs y CompanyTeamAccessClient.cs).

export type TeamContext = {
  organizationId: number;
  userId: number;
  timeZone: string;
  team: boolean;
  manageInboxes: boolean;
  manageRoles: boolean;
};
export type Page<T> = { items: T[]; total: number; page: number; pageSize: number };
export type MemberInbox = { inboxId: number; name: string; role: string | null; canTake?: boolean };
export type MemberPause = {
  individual: boolean;
  until: string | null;
  groupBlocked: boolean;
  groupNames: string[];
  groupUntil?: string | null;
};
export type AccessSummary = {
  userId: number;
  chatRole: string | null;
  inboxes: number;
  receivingNow: number;
  assignment: string;
  availability: string;
  reason: string | null;
  until?: string | null;
};
export type Member = {
  userId: number;
  name: string;
  email: string;
  status: string;
  roles: string[];
  hasAssignmentAuthority?: boolean;
  canReceive?: boolean;
  causes?: string[] | null;
  canPause?: boolean;
  inboxNames?: string[] | null;
  roleNames?: string[] | null;
  inboxes?: MemberInbox[] | null;
  pause?: MemberPause | null;
  hasChatTake?: boolean;
  canManage?: boolean;
  summary?: AccessSummary | null;
  accessState: string;
};
export type Group = {
  groupId: number;
  name: string;
  description: string | null;
  scheduleId: number | null;
  active: boolean;
  version: number;
  memberCount: number;
};
export type GroupRole = { roleId: number; name: string; permissions: string[]; code: string };
export type ConfiguredRole = {
  roleId: number;
  code: string;
  name: string;
  description: string | null;
  operational: boolean;
  protected: boolean;
  ownDefinition: boolean;
  version: number;
  permissions: string[];
  memberCount: number;
  actorUsesRole?: boolean;
  memberIds?: number[] | null;
};
export type Interval = { day: number; startMinute: number; endMinute: number };
export type Schedule = {
  scheduleId: number;
  name: string;
  timeZone: string;
  intervals: Interval[];
  version: number;
  active: boolean;
};
export type ScheduleLinks = { groupIds: number[]; userIds: number[]; inboxIds: number[]; expectedVersion: number };
export type Participation = {
  participationId: number;
  groupId: number;
  groupName: string;
  userId: number;
  roleId: number;
  scheduleOverrideId: number | null;
  effectiveScheduleId: number | null;
  active: boolean;
  version: number;
};
export type Pause = {
  pauseId: number;
  groupId: number | null;
  userId: number | null;
  reason: string;
  startedAt: string;
  endsAt: string | null;
  resumedAt: string | null;
  actorUserId: number;
  continuity: string;
  releasedCount: number;
};
export type PauseImpact = { authorizedOpenCount: number; canRelease: boolean };
export type Closure = {
  closureId: number;
  groupId: number | null;
  name: string;
  timeZone: string;
  startsAt: string;
  endsAt: string;
  active: boolean;
  version: number;
  groupIds?: number[] | null;
};
export type InboxRow = { inboxId: number; name: string; status: string; assignmentMode: string };
export type PersonChatRole = { roleId: number; code: string; name: string; permissions: string[]; memberIds: number[] };
export type ExceptionState = { state: string; reason?: string | null };
export type ConfigurationChange = { actorUserId: number; actorName: string; at: string };
export type AgentConfiguration = {
  principalRoleId: number | null;
  principalCode: string | null;
  take: ExceptionState;
  assign: ExceptionState;
  stamp: number;
  lastChange: ConfigurationChange | null;
  canManage: boolean;
};
export type InboxAction = { action: string; allowed: boolean; reason: string | null };
export type Reception = {
  assignment: string;
  assignmentReason: string | null;
  availability: string;
  availabilityReason: string | null;
  until?: string | null;
};
export type InboxAccess = {
  inboxId: number;
  name: string;
  function: string | null;
  mode: string;
  participatesInRouting: boolean;
  takesUnassigned: boolean;
  viewsUnassigned: boolean;
  maxActiveSessions: number | null;
  openSessions: number;
  actions: InboxAction[];
  reception: Reception;
};
export type ActionExplanation = {
  action: string;
  result: string;
  origin: string;
  originRef: string | null;
  reason: string | null;
};
export type EffectiveAccess = {
  userId: number;
  accessState: string;
  companyRole: string | null;
  chatRole: { code: string; name: string; source: string } | null;
  actions: ActionExplanation[];
  modules: { code: string; decision: string }[];
  inboxes: InboxAccess[];
  summary: AccessSummary;
  evaluatedAt: string;
};
export type DraftInbox = {
  inboxId: number;
  function: string;
  participatesInRouting: boolean;
  takesUnassigned: boolean;
  viewsUnassigned: boolean;
};
export type AccessPreview = {
  current: EffectiveAccess;
  draft: EffectiveAccess;
  gains: { action: string; inboxId: number; inboxName: string }[];
};
export type Zone = { id: string; label: string };

/** Respuesta de una operación: el valor, o el código de falla como lo leía CompanyOperatingClient (OperatingReply). */
export type Reply<T> = { value: T | null; failureCode: string | null; status: number };

/** Falla de un pedido de Equipo con su estado HTTP y su código. */
export class TeamFailure extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    readonly body: unknown = null,
  ) {
    super(code);
  }
}

export class TeamApi {
  constructor(readonly tenant: number) {}

  /** GET de operating-configuration (los mismos caminos que CompanyOperatingClient). */
  get<T>(path: string): Promise<Reply<T>> {
    return this.reply<T>("GET", `op/${path}`);
  }
  /** POST de operating-configuration con el borrador (el servidor pone el actor y su sesión). */
  post<T>(path: string, draft: unknown): Promise<Reply<T>> {
    return this.reply<T>("POST", `op/${path}`, draft);
  }

  inboxes(): Promise<InboxRow[]> {
    return this.json("GET", "inboxes");
  }
  chatRoles(): Promise<PersonChatRole[]> {
    return this.json("GET", "chat-roles");
  }
  zones(): Promise<Zone[]> {
    return (zonesCache ??= this.json<Zone[]>("GET", "timezones").catch((error: unknown) => {
      zonesCache = null;
      throw error;
    }));
  }
  access(member: number): Promise<EffectiveAccess> {
    return this.json("GET", `members/${member}/access`);
  }
  preview(member: number, draft: unknown): Promise<AccessPreview> {
    return this.json("POST", `members/${member}/access/preview`, draft);
  }
  configuration(member: number): Promise<AgentConfiguration> {
    return this.json("GET", `members/${member}/configuration`);
  }
  saveConfiguration(member: number, draft: unknown): Promise<AgentConfiguration> {
    return this.json("POST", `members/${member}/configuration`, draft);
  }
  grantChat(member: number): Promise<void> {
    return this.json("POST", `members/${member}/chat-access`, {});
  }
  setInboxes(member: number, inboxes: DraftInbox[]): Promise<void> {
    return this.json("POST", `members/${member}/inboxes`, { inboxes });
  }
  refresh(member: number): Promise<void> {
    return this.json("POST", `members/${member}/refresh`, {});
  }
  internalChat(member: number): Promise<{ caseGroupId: number }> {
    return this.json("POST", `members/${member}/internal-chat`, {});
  }
  removeFromChat(
    member: number,
    inboxes: { inboxId: number; name: string; role: string }[],
  ): Promise<{ undo: string }> {
    return this.json("POST", `members/${member}/remove-from-chat`, { inboxes });
  }
  undoRemove(undo: string): Promise<void> {
    return this.json("POST", "remove-from-chat/undo", { undo });
  }

  private async send(method: string, path: string, body?: unknown): Promise<Response> {
    const headers: Record<string, string> = { Accept: "application/json", "X-Bheli-Team-Tenant": String(this.tenant) };
    if (method !== "GET") {
      headers["Content-Type"] = "application/json";
      headers["X-CSRF-TOKEN"] = getAntiforgeryToken() ?? "";
    }
    return fetch(`/api/portal/team/${path}`, {
      method,
      headers,
      credentials: "same-origin",
      body: body === undefined ? undefined : JSON.stringify(body),
    });
  }

  private async reply<T>(method: string, path: string, body?: unknown): Promise<Reply<T>> {
    const response = await this.send(method, path, body);
    const text = await response.text();
    if (response.ok)
      return { value: text ? (JSON.parse(text) as T) : null, failureCode: null, status: response.status };
    // Sin código legible, como CompanyOperatingClient: «UNAVAILABLE».
    let code = "UNAVAILABLE";
    try {
      code = (JSON.parse(text) as { code?: string }).code ?? "UNAVAILABLE";
    } catch {
      /* cuerpo vacío o no JSON */
    }
    if (response.status === 409 && code === "TENANT_CHANGED") throw new TeamFailure(409, code);
    return { value: null, failureCode: code, status: response.status };
  }

  private async json<T>(method: string, path: string, body?: unknown): Promise<T> {
    const response = await this.send(method, path, body);
    const text = await response.text();
    let parsed: unknown = null;
    try {
      parsed = text ? JSON.parse(text) : null;
    } catch {
      parsed = null;
    }
    if (!response.ok) {
      const code = (parsed as { code?: string } | null)?.code ?? "UNAVAILABLE";
      throw new TeamFailure(response.status, code, parsed);
    }
    return parsed as T;
  }
}

let zonesCache: Promise<Zone[]> | null = null;

/** Contexto de la pantalla: empresa, persona, zona y qué puede abrir (lo decide el servidor). */
export async function loadTeamContext(): Promise<TeamContext> {
  const response = await fetch("/api/portal/team/context", { credentials: "same-origin" });
  if (!response.ok) throw new TeamFailure(response.status, "UNAVAILABLE");
  return (await response.json()) as TeamContext;
}
